import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  budgetsApi,
  credentialsApi,
  providersApi,
  sitesApi,
  workflowConfigApi,
} from '@/services/config';
import type {
  BudgetUpsert,
  CredentialCreate,
  CredentialRotate,
  CredentialUpdate,
  WorkflowConfigUpsert,
  WpSiteCreate,
  WpSiteUpdate,
} from '@/types/config';

export const configKeys = {
  sites: ['config', 'sites'] as const,
  site: (id: string) => ['config', 'sites', id] as const,
  credentials: (provider?: string) =>
    ['config', 'credentials', { provider }] as const,
  credential: (id: string) => ['config', 'credentials', id] as const,
  providers: ['config', 'providers'] as const,
  provider: (name: string) => ['config', 'providers', name] as const,
  workflowConfig: (id: string) => ['config', 'workflows', id] as const,
  budgets: ['config', 'budgets'] as const,
  budgetHistory: (months: number) =>
    ['config', 'budgets', 'history', months] as const,
};

// ─── sites ────────────────────────────────────────────────────────────────

export function useSites(activeOnly = false) {
  return useQuery({
    queryKey: [...configKeys.sites, { activeOnly }],
    queryFn: () => sitesApi.list(activeOnly),
  });
}

export function useSite(id: string | undefined) {
  return useQuery({
    queryKey: configKeys.site(id ?? ''),
    queryFn: () => sitesApi.get(id!),
    enabled: Boolean(id),
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WpSiteCreate) => sitesApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.sites });
    },
  });
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: WpSiteUpdate }) =>
      sitesApi.update(id, payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: configKeys.sites });
      qc.invalidateQueries({ queryKey: configKeys.site(id) });
    },
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sitesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.sites });
    },
  });
}

export function useTestSite() {
  return useMutation({
    mutationFn: (id: string) => sitesApi.test(id),
  });
}

// ─── credentials ──────────────────────────────────────────────────────────

export function useCredentials(provider?: string) {
  return useQuery({
    queryKey: configKeys.credentials(provider),
    queryFn: () => credentialsApi.list(provider),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CredentialCreate) =>
      credentialsApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', 'credentials'] });
    },
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: CredentialUpdate;
    }) => credentialsApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', 'credentials'] });
    },
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => credentialsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', 'credentials'] });
    },
  });
}

export function useRotateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: CredentialRotate;
    }) => credentialsApi.rotate(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['config', 'credentials'] });
    },
  });
}

export function useRevealCredential() {
  return useMutation({
    mutationFn: (id: string) => credentialsApi.reveal(id),
  });
}

// ─── provider settings ────────────────────────────────────────────────────

export function useProviderSettings() {
  return useQuery({
    queryKey: configKeys.providers,
    queryFn: () => providersApi.list(),
  });
}

export function useUpsertProviderSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      config,
    }: {
      provider: string;
      config: Record<string, unknown>;
    }) => providersApi.upsert(provider, config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.providers });
    },
  });
}

// ─── workflow config overlay ──────────────────────────────────────────────

export function useWorkflowConfig(workflowId: string | undefined) {
  return useQuery({
    queryKey: configKeys.workflowConfig(workflowId ?? ''),
    queryFn: () => workflowConfigApi.get(workflowId!),
    enabled: Boolean(workflowId),
  });
}

export function useUpsertWorkflowConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workflowId,
      payload,
    }: {
      workflowId: string;
      payload: WorkflowConfigUpsert;
    }) => workflowConfigApi.upsert(workflowId, payload),
    onSuccess: (_, { workflowId }) => {
      qc.invalidateQueries({
        queryKey: configKeys.workflowConfig(workflowId),
      });
    },
  });
}

// ─── budgets ──────────────────────────────────────────────────────────────

export function useBudgets() {
  return useQuery({
    queryKey: configKeys.budgets,
    queryFn: () => budgetsApi.list(),
    refetchInterval: 5 * 60_000,
  });
}

export function useBudgetHistory(months = 6) {
  return useQuery({
    queryKey: configKeys.budgetHistory(months),
    queryFn: () => budgetsApi.history(months),
  });
}

export function useUpsertBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      provider,
      payload,
    }: {
      provider: string;
      payload: BudgetUpsert;
    }) => budgetsApi.upsert(provider, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: configKeys.budgets });
    },
  });
}
